import { rewritePlan } from '@timelog/pm-shared';
import type { PlanNode } from '@timelog/pm-shared';

/**
 * Stamps newly inserted leaves with the project's contract (ADR 0033: the
 * project default is a creation-time prefill, never a read-time fallback).
 *
 * It runs AFTER the insertion, not on the node before it, and only fills a
 * contract that is still absent. That ordering matters: adding a subtask under
 * a mapped task hands the parent's contract down to the child
 * (`insertChildWithTransfer`, BR-2), and a task should inherit the contract it
 * is being nested under rather than the project-wide prefill. Filling the node
 * first would have silently won that race.
 *
 * Only childless nodes are stamped. The capture composer inserts whole
 * subtrees, whose depth-0 lines immediately have children — BR-2 refuses a
 * contract on those, so stamping them would 400 on the next save.
 */
export const applyContractPrefill = (
  plan: PlanNode[],
  insertedIds: ReadonlySet<string>,
  contractId: string | undefined,
): PlanNode[] => {
  if (contractId === undefined || insertedIds.size === 0) return plan;

  const { plan: next, changed } = rewritePlan(plan, (node) =>
    insertedIds.has(node.id) && node.children.length === 0 && node.contractId === undefined
      ? { ...node, contractId }
      : node,
  );
  return changed ? next : plan;
};

/**
 * Every id in the given subtrees, so an insertion can stamp the leaves it just
 * added without walking the whole plan for them.
 *
 * Iterative: plans hold tens of thousands of tasks and a recursive walk blows
 * the stack (BR-3).
 */
export const subtreeIds = (nodes: readonly PlanNode[]): Set<string> => {
  const ids = new Set<string>();
  const stack: PlanNode[] = [...nodes];
  while (stack.length > 0) {
    const node = stack.pop()!;
    ids.add(node.id);
    for (const child of node.children) stack.push(child);
  }
  return ids;
};
